import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResultPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isCancel = location.pathname.includes('cancel');
  const [status, setStatus] = useState(isCancel ? 'cancelled' : 'checking');
  const attempts = useRef(0);

  useEffect(() => {
    if (isCancel) return;
    const sessionId = new URLSearchParams(location.search).get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('error');
          return;
        }
      } catch (e) {
        // keep polling
      }
      if (attempts.current < 8) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus('timeout');
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [isCancel, location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="max-w-md w-full border-2">
        <CardContent className="p-8 text-center space-y-4">
          {status === 'checking' && (
            <>
              <Loader2 className="h-14 w-14 mx-auto animate-spin text-primary" />
              <h1 className="text-2xl font-bold" data-testid="payment-checking">Confirming your payment...</h1>
              <p className="text-muted-foreground">This only takes a few seconds.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle2 className="h-14 w-14 mx-auto text-green-600" />
              <h1 className="text-2xl font-bold" data-testid="payment-success">Subscription Active!</h1>
              <p className="text-muted-foreground">
                Your plan is now active. All included features are unlocked.
              </p>
              <Button className="w-full" onClick={() => navigate('/settings')} data-testid="back-to-settings-btn">
                Back to Settings
              </Button>
            </>
          )}
          {status === 'cancelled' && (
            <>
              <XCircle className="h-14 w-14 mx-auto text-amber-500" />
              <h1 className="text-2xl font-bold" data-testid="payment-cancelled">Checkout Cancelled</h1>
              <p className="text-muted-foreground">No charge was made. You can choose a plan anytime.</p>
              <Button className="w-full" onClick={() => navigate('/settings')} data-testid="back-to-settings-btn">
                Back to Settings
              </Button>
            </>
          )}
          {(status === 'error' || status === 'timeout') && (
            <>
              <XCircle className="h-14 w-14 mx-auto text-red-500" />
              <h1 className="text-2xl font-bold" data-testid="payment-error">
                {status === 'timeout' ? 'Still processing' : 'Something went wrong'}
              </h1>
              <p className="text-muted-foreground">
                {status === 'timeout'
                  ? 'Your payment may still be processing. Check Settings in a minute.'
                  : 'We could not confirm the payment. Please try again.'}
              </p>
              <Button className="w-full" onClick={() => navigate('/settings')} data-testid="back-to-settings-btn">
                Back to Settings
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
